import React, { Suspense, lazy } from "react";
import { useRoutes } from "react-router-dom";
import ProtectedRoute from "../components/ProtectedRoute";
import GenerateTraining from "../pages/generate-training/GenerateTraining";
import PlanOptimizer from "../pages/plan-optimizer/PlanOptimizer";
import TrainingCatalogue from "../pages/training-catalogue/TrainingCatalogue";
import CompanyCatalogue from "../pages/training-catalogue/CompanyCatalogue";
import IFundiCatalogue from "../pages/training-catalogue/IFundiCatalogue";
import CriticalSkills from "../pages/training-catalogue/CriticalSkills";
import AddTrainingCatalogue from "../pages/training-catalogue/AddTrainingCatalogue";
import AssignCriticalTrainingPage from "../pages/training-catalogue/AssignCriticalTrainingPage";
import RequestedCatalogue from "../pages/training-catalogue/RequestedCatalogue";
import EmployeeTrainingImport from "../pages/employee-training-import/EmployeeTrainingImport";
import OrganogramList from "../pages/organogram/OrganogramList";
import OrganogramTree from "../pages/organogram/OrganogramTree";
import TrainingConfiguration from "../pages/training-configuration/TrainingConfiguration";
import FormConfiguration from "../pages/training-configuration/FormConfiguration";
import BudgetConfiguration from "../pages/training-configuration/BudgetConfiguration";
import BBBudgetConfigurationPage from "../pages/training-configuration/BBBudgetConfigurationPage";
import TrainingTypeBudgetConfiguration from "../pages/training-configuration/TrainingTypeBudgetConfiguration";
import TrainingCategoryBudgetConfiguration from "../pages/training-configuration/TrainingCategoryBudgetConfiguration";
import ReviewTrainingNeeds from "../pages/training-configuration/ReviewTrainingNeeds";
import BulkTrainingAllocation from "../pages/training-configuration/BulkTrainingAllocation";
import EditTrainingNeeds from "../pages/training-configuration/EditTrainingNeeds";
import EmployeeForm from "../pages/training-configuration/EmployeeForm";
import { COMPANY_PRIVILEGED_ROLES, roleType } from "../constant/constants";
import SetaSheet from "../pages/setaTemplate/SetaSheet";
import StrategicInputForm from "../pages/training-strategy/StrategicInputForm";
import CompanySetup from "../pages/company-setup/CompanySetup";
import SuperAdmin from "../pages/training-catalogue/SuperAdmin";
import NotFoundRedirect from "../components/NotFoundRedirect";
import FakeGenerateTraining from "../pages/generate-training/FakeGenerateTraining";
// ----------------------------------------------------------------------
const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<>Loading...</>}>
      <Component {...props} />
    </Suspense>
  );
// ----------------------------------------------------------------------

const LoginPage = Loadable(lazy(() => import("../pages/auth/Login")));
const DashboardPage = Loadable(
  lazy(() => import("../pages/dashboard/Dashboard"))
);
const ForgotPassword = Loadable(
  lazy(() => import("../pages/auth/ForgotPassword"))
);
const ResetPassword = Loadable(
  lazy(() => import("../pages/auth/ResetPassword"))
);

const CreateUser = Loadable(lazy(() => import("../pages/user/CreateUser")));
const UserList = Loadable(lazy(() => import("../pages/user/UserList")));
const CreateCompany = Loadable(
  lazy(() => import("../pages/company/CreateCompany"))
);
const CompanyList = Loadable(
  lazy(() => import("../pages/company/CompanyList"))
);

const CreateSdfUser = Loadable(
  lazy(() => import("../pages/sdfUsers/CreateSdfUser"))
);

const SdfList = Loadable(lazy(() => import("../pages/sdfUsers/SdfList")));
const CompanyDetails = Loadable(
  lazy(() => import("../pages/company/CompanyDetails"))
);
const EmployeeList = Loadable(
  lazy(() => import("../pages/employee/CreateEmployee"))
);
const UpdateEmployee = Loadable(
  lazy(() => import("../pages/employee/UpdateEmployee"))
);
const SetaList = Loadable(lazy(() => import("../pages/setaTemplate/SetaList")));
const CreateTraining = Loadable(
  lazy(() => import("../pages/generate-training/CreateTraining"))
);
const FakeGenerate = Loadable(
  lazy(() => import("../pages/generate-training/FakeGenerateTraining"))
);

const EmployeeFormStatus = Loadable(
  lazy(() => import("../pages/training-configuration/EmployeeFormStatus"))
);

const PromptMaster = Loadable(lazy(() => import("../pages/prompt-master/PromptMaster")));
const ParserLabPage = Loadable(
  lazy(() => import("../pages/parser-lab/ParserLabPage"))
);

const Router = () => {
  return useRoutes([
    {
      path: "/",
      element: <LoginPage />,
    },
    {
      path: "/dashboard",
      element: (
        <ProtectedRoute
          roles={[...COMPANY_PRIVILEGED_ROLES, roleType.MANAGER]}
        >
          <DashboardPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "/generate-training",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <GenerateTraining />
          {/* <FakeGenerate /> */}
        </ProtectedRoute>
      ),
    },
    {
      path: "/plan-optimizer",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <PlanOptimizer />
        </ProtectedRoute>
      ),
    },
    {
      path: "/parser-lab",
      element: (
        <ProtectedRoute roles={COMPANY_PRIVILEGED_ROLES}>
          <ParserLabPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "/forgot-password",
      element: <ForgotPassword />,
    },
    {
      path: "/reset-password",
      element: <ResetPassword />,
    },
    {
      path: "/set-password",
      element: <ResetPassword />,
    },
    {
      path: "//reset-password",
      element: <ResetPassword />,
    },
    {
      path: "//set-password",
      element: <ResetPassword />,
    },
    {
      path: "/create-user/:userId",
      element: (
        <ProtectedRoute roles={[...COMPANY_PRIVILEGED_ROLES, roleType.ADMIN]}>
          <CreateUser />
        </ProtectedRoute>
      ),
    },
    {
      path: "/user-list",
      element: (
        <ProtectedRoute roles={[...COMPANY_PRIVILEGED_ROLES, roleType.ADMIN]}>
          <UserList />
        </ProtectedRoute>
      ),
    },
    {
      path: "/company-setup/:companyId",
      element: (
        <ProtectedRoute roles={[roleType.ADMIN]}>
          <CreateCompany />
        </ProtectedRoute>
      ),
    },
    {
      path: "/company-list",
      element: (
        <ProtectedRoute roles={[roleType.ADMIN]}>
          <CompanyList />
        </ProtectedRoute>
      ),
    },
    {
      path: "/edit-sdf-user/:sdfUserId",
      element: (
        <ProtectedRoute roles={[roleType.ADMIN]}>
          <CreateSdfUser />
        </ProtectedRoute>
      ),
    },
    {
      path: "/create-sdf-user/:companyId",
      element: (
        <ProtectedRoute roles={[roleType.ADMIN]}>
          <CreateSdfUser />
        </ProtectedRoute>
      ),
    },
    {
      path: "/sdf-list",
      element: (
        <ProtectedRoute roles={[roleType.ADMIN]}>
          <SdfList />
        </ProtectedRoute>
      ),
    },
    {
      path: "/company-details",
      element: (
        <ProtectedRoute
          roles={[roleType.ADMIN, ...COMPANY_PRIVILEGED_ROLES]}
        >
          <CompanyDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: "/employee-list",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <EmployeeList />
        </ProtectedRoute>
      ),
    },
    {
      path: "/edit/:employeeId",
      element: (
        <ProtectedRoute
          roles={[...COMPANY_PRIVILEGED_ROLES, roleType.ADMIN]}
        >
          <UpdateEmployee />
        </ProtectedRoute>
      ),
    },
    {
      path: "/add",
      element: (
        <ProtectedRoute
          roles={[...COMPANY_PRIVILEGED_ROLES, roleType.ADMIN]}
        >
          <UpdateEmployee />
        </ProtectedRoute>
      ),
    },
    {
      path: "/seta-list",
      element: (
        <ProtectedRoute
          roles={[roleType.ADMIN]}
        >
          <SetaList />
        </ProtectedRoute>
      ),
    },
     {
      path: "/seta-sheet",
      element: (
        <ProtectedRoute roles={[roleType.ADMIN]}>
          <SetaSheet />
        </ProtectedRoute>
      ),
    },
    {
      path: "/training-catalogue",
      element: (
        <ProtectedRoute
          roles={[roleType.ADMIN, ...COMPANY_PRIVILEGED_ROLES]}
        >
          <TrainingCatalogue />
        </ProtectedRoute>
      ),
    },
    {
      path: "/company-catalogue",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <CompanyCatalogue />
        </ProtectedRoute>
      ),
    },
    {
      path: "/ifundi-catalogue",
      element: (
        <ProtectedRoute
          roles={[roleType.ADMIN, ...COMPANY_PRIVILEGED_ROLES]}
        >
          <IFundiCatalogue />
        </ProtectedRoute>
      ),
    },
    {
      path: "/critical-skills",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <CriticalSkills />
        </ProtectedRoute>
      ),
    },
    {
      path: "/add-training-catalogue",
      element: (
        <ProtectedRoute
          roles={[roleType.ADMIN, ...COMPANY_PRIVILEGED_ROLES]}
        >
          <AddTrainingCatalogue />
        </ProtectedRoute>
      ),
    },
    {
      path: "/assign-critical-training",
      element: (
        <ProtectedRoute
          roles={[roleType.ADMIN, ...COMPANY_PRIVILEGED_ROLES]}
        >
          <AssignCriticalTrainingPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "/requested-training",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <RequestedCatalogue />
        </ProtectedRoute>
      ),
    },
    {
      path: "/employee-training-import",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <EmployeeTrainingImport />
        </ProtectedRoute>
      ),
    },
    {
      path: "/organogram-list",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <OrganogramList />
        </ProtectedRoute>
      ),
    },
    {
      path: "/organogram-tree",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <OrganogramTree />
        </ProtectedRoute>
      ),
    },
    {
      path: "/training-configuration",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <TrainingConfiguration />
        </ProtectedRoute>
      ),
    },
    {
      path: "/form-configuration",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <FormConfiguration />
        </ProtectedRoute>
      ),
    },
    {
      path: "/budget-configuration",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <BudgetConfiguration />
        </ProtectedRoute>
      ),
    },
    {
      path: "/bbbee-budget-configuration",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <BBBudgetConfigurationPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "/training-type-budget-configuration",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <TrainingTypeBudgetConfiguration />
        </ProtectedRoute>
      ),
    },
    {
      path: "/training-category-budget-configuration",
      element: (
        <ProtectedRoute
          roles={COMPANY_PRIVILEGED_ROLES}
        >
          <TrainingCategoryBudgetConfiguration />
        </ProtectedRoute>
      ),
    },
    {
      path: "/review-training-needs",
      element: (
        <ProtectedRoute roles={[...COMPANY_PRIVILEGED_ROLES, roleType.MANAGER]}>
          <ReviewTrainingNeeds />
        </ProtectedRoute>
      ),
    },
    {
      path: "/bulk-training-allocation",
      element: (
        <ProtectedRoute roles={COMPANY_PRIVILEGED_ROLES}>
          <BulkTrainingAllocation />
        </ProtectedRoute>
      ),
    },
    {
      path: "/training-form/:id?",
      element: (
        <ProtectedRoute
          roles={[roleType.EMPLOYEE, ...COMPANY_PRIVILEGED_ROLES, roleType.MANAGER]}
        >
          <EditTrainingNeeds />
        </ProtectedRoute>
      ),
    },
    {
      path: "/form/:id?",
      element: (
        <ProtectedRoute
          roles={[...COMPANY_PRIVILEGED_ROLES, roleType.MANAGER, roleType.EMPLOYEE]}
        >
          <EmployeeForm />
        </ProtectedRoute>
      ),
    },
    {
      path: "/employee-form-status",
      element: (
        <ProtectedRoute
          roles={[...COMPANY_PRIVILEGED_ROLES, roleType.MANAGER, roleType.EMPLOYEE]}
        >
          <EmployeeFormStatus />
        </ProtectedRoute>
      ),
    },
    {
      path: "/strategic-input-form",
      element: (
        <ProtectedRoute roles={COMPANY_PRIVILEGED_ROLES}>
          <StrategicInputForm />
        </ProtectedRoute>
      ),
    },
    {
      path: "/company-setup",
      element: (
        <ProtectedRoute roles={[...COMPANY_PRIVILEGED_ROLES, roleType.ADMIN]}>
          <CompanySetup />
        </ProtectedRoute>
      ),
    },
    {
      path: "/prompt-master",
      element: (
        <ProtectedRoute roles={[roleType.ADMIN]}>
          <PromptMaster />
        </ProtectedRoute>
      ),
    },
    {
      path: "/eap-configuration",
      element: (
        <ProtectedRoute roles={[roleType.ADMIN]}>
          <SuperAdmin />
        </ProtectedRoute>
      ),
    },
    // Catch-all route for undefined paths
    {
      path: "*",
      element: (
        <ProtectedRoute roles={[roleType.ADMIN, roleType.EMPLOYEE, ...COMPANY_PRIVILEGED_ROLES, roleType.MANAGER]}>
          <NotFoundRedirect />
        </ProtectedRoute>
      ),
    },
  ]);
};

export default Router;
